import React, { useState, useEffect } from 'react';
import { Truck } from 'lucide-react';
import { MerchantInvoice } from '../../types/gst';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  invoice: MerchantInvoice | null;
  onConfirmEwb: (vehicleNo: string, transporterId: string, distanceKm: number) => Promise<void>;
  isProcessing: boolean;
}

export const EwbConsignmentModal: React.FC<Props> = ({
  isOpen,
  onClose,
  invoice,
  onConfirmEwb,
  isProcessing
}) => {
  if (!isOpen || !invoice) return null;

  const [vehicleNo, setVehicleNo] = useState<string>(invoice.eWayBill?.vehicleNumber || 'MH-04-AB-1234');
  const [transporterId, setTransporterId] = useState<string>(invoice.eWayBill?.transporterId || '27AAACG0500P1ZT');
  const [distanceKm, setDistanceKm] = useState<number>(invoice.eWayBill?.distanceKm || 120);

  useEffect(() => {
    if (invoice?.eWayBill) {
      setVehicleNo(invoice.eWayBill.vehicleNumber || 'MH-04-AB-1234');
      setTransporterId(invoice.eWayBill.transporterId || '27AAACG0500P1ZT');
      setDistanceKm(invoice.eWayBill.distanceKm || 120);
    }
  }, [invoice]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onConfirmEwb(vehicleNo, transporterId, distanceKm);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-md rounded-2xl border border-white/10 bg-[#131316] p-6 shadow-2xl space-y-4 text-xs">
        <div className="flex items-center justify-between border-b border-white/10 pb-3">
          <div className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-cyan-400" />
            <div>
              <h3 className="font-bold text-white text-sm">Generate Rule 138 E-Way Bill</h3>
              <p className="text-[11px] text-slate-400">Invoice #{invoice.invoiceNumber}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-white">
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-[10px] font-bold uppercase text-slate-400 block mb-1">
              Vehicle Number (Part-B) *
            </label>
            <input
              type="text"
              value={vehicleNo}
              onChange={(e) => setVehicleNo(e.target.value.toUpperCase())}
              placeholder="e.g. MH-04-AB-1234"
              className="w-full rounded-xl border border-white/10 bg-[#0e1015] px-3 py-2 text-white font-mono uppercase focus:outline-none focus:border-cyan-500"
              required
            />
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase text-slate-400 block mb-1">
              Transporter ID / GSTIN
            </label>
            <input
              type="text"
              value={transporterId}
              onChange={(e) => setTransporterId(e.target.value.toUpperCase())}
              placeholder="e.g. 27AAACG0500P1ZT"
              className="w-full rounded-xl border border-white/10 bg-[#0e1015] px-3 py-2 text-white font-mono uppercase focus:outline-none focus:border-cyan-500"
            />
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase text-slate-400 block mb-1">
              Estimated Transport Distance (km)
            </label>
            <input
              type="number"
              min="1"
              value={distanceKm}
              onChange={(e) => setDistanceKm(Number(e.target.value))}
              className="w-full rounded-xl border border-white/10 bg-[#0e1015] px-3 py-2 text-white font-mono focus:outline-none focus:border-cyan-500"
              required
            />
            <p className="text-[9px] text-slate-500 mt-1">Rule 138: 1 day validity granted per 200 km</p>
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-white/10">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-slate-300 font-medium transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isProcessing}
              className="px-4 py-2 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-black font-bold transition shadow-sm"
            >
              {isProcessing ? 'Generating...' : 'Issue E-Way Bill'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
